import { motion } from "framer-motion";
import { Truck, Dna, TestTube, Stamp, Syringe, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "medical-courier",
    icon: Truck,
    title: "Medical Courier",
    tag: "Logistics",
    body: "STAT & scheduled transport of specimens, pharmaceuticals, and medical records with sealed chain-of-custody.",
    img: "https://images.unsplash.com/photo-1543499459-d1460946bdc6",
    span: "md:col-span-7",
  },
  {
    id: "dna-testing",
    icon: Dna,
    title: "DNA Testing",
    tag: "Diagnostics",
    body: "Accredited paternity, relationship & legal DNA collection with defensible custody.",
    img: "https://images.unsplash.com/photo-1639772823849-6efbd173043c",
    span: "md:col-span-5",
  },
  {
    id: "drug-testing",
    icon: TestTube,
    title: "Drug Testing",
    tag: "Compliance",
    body: "DOT & non-DOT panels, pre-employment and random screening with fast turnaround.",
    img: "https://images.unsplash.com/photo-1614935151651-0bea6508db6b",
    span: "md:col-span-5",
  },
  {
    id: "notary-services",
    icon: Stamp,
    title: "Notary Services",
    tag: "Legal",
    body: "Mobile & remote notarization for medical, legal, and personal documents — we come to you.",
    img: "https://images.unsplash.com/photo-1664463760781-f159dfe3af30",
    span: "md:col-span-7",
  },
];

const Card = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.div
      data-testid={`service-${s.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
      className={`group relative border border-border bg-card overflow-hidden ${s.span}`}
    >
      <div className="relative h-52 md:h-64 overflow-hidden border-b border-border">
        <img
          src={s.img}
          alt={s.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-700"
        />
        <div className="absolute top-4 left-4 bg-background border border-border px-3 py-1.5">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">{s.tag}</span>
        </div>
      </div>
      <div className="p-7 md:p-10">
        <div className="flex items-start justify-between mb-5">
          <Icon size={28} strokeWidth={1.4} className="text-primary" />
          <ArrowUpRight size={22} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
        </div>
        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-3">{s.title}</h3>
        <p className="text-sm md:text-base font-mono text-muted-foreground leading-relaxed">{s.body}</p>
      </div>
    </motion.div>
  );
};

export const Services = () => (
  <section id="services" className="py-24 md:py-40">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ Capabilities</span>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase mt-5 leading-none">
            Services <span className="text-primary">Suite</span>
          </h2>
        </div>
        <p className="text-sm font-mono text-muted-foreground max-w-md md:text-right">
          Four integrated services, one accountable partner — engineered for the medical and legal sectors.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {SERVICES.map((s, i) => (
          <Card key={s.id} s={s} i={i} />
        ))}

        {/* Phlebotomy coming soon */}
        <motion.div
          data-testid="service-phlebotomy"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative border border-border md:col-span-12 overflow-hidden bg-foreground text-background"
        >
          <div className="grid grid-cols-1 md:grid-cols-12">
            <div className="md:col-span-4 relative h-56 md:h-auto overflow-hidden border-b md:border-b-0 md:border-r border-background/20">
              <img
                src="https://images.unsplash.com/photo-1615461065624-21b562ee5566"
                alt="Phlebotomy"
                className="w-full h-full object-cover grayscale opacity-40"
              />
            </div>
            <div className="md:col-span-8 p-8 md:p-14 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-5">
                <Syringe size={26} strokeWidth={1.4} className="text-accent" />
                <span className="text-[10px] font-mono uppercase tracking-[0.3em] bg-accent text-accent-foreground px-3 py-1.5">Coming Soon</span>
              </div>
              <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter uppercase mb-4">Mobile Phlebotomy</h3>
              <p className="text-sm md:text-base font-mono text-background/60 leading-relaxed max-w-2xl">
                On-site blood draw and specimen collection is joining the BioFleet suite — closing the loop from collection to laboratory with a single custody chain. Register your interest below.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
